import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger
} from "@/components/ui/tooltip";
import { cn } from "@/lib/utils";
import React from "react";
import { useNavigate } from "react-router-dom";

interface Step {
  id: string;
  title: string;
  href: string;
  status: "completed" | "current" | "upcoming";
}

interface StepIndicatorProps {
  currentStep: number;
  steps: Step[];
  className?: string;
}

export function StepIndicator({ steps, className }: StepIndicatorProps) {
  const navigate = useNavigate();

  const handleStepClick = (step: Step) => {
    // Allow navigation to any step (all are accessible)
    navigate(step.href);
  };

  return (
    <div className={cn("w-full", className)}>
      <TooltipProvider>
        <div className="flex items-center justify-center space-x-2 md:space-x-4">
          {steps.map((step, index) => {
            const stepNumber = index + 1;
            const isCompleted = step.status === "completed";
            const isCurrent = step.status === "current";
            const isUpcoming = step.status === "upcoming";

            return (
              <React.Fragment key={step.id}>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <button
                      onClick={() => handleStepClick(step)}
                      className={cn(
                        "flex items-center justify-center w-8 h-8 rounded-full text-sm font-medium transition-all duration-200 cursor-pointer hover:scale-110",
                        isCompleted &&
                          "bg-green-600 text-white hover:bg-green-700",
                        isCurrent &&
                          "bg-[#d769b3] text-white hover:bg-[#c055a0] ring-2 ring-[#d769b3]/30",
                        isUpcoming &&
                          "bg-gray-200 text-gray-600 hover:bg-gray-300"
                      )}
                    >
                      {stepNumber}
                    </button>
                  </TooltipTrigger>
                  <TooltipContent id={step.id}>
                    <p className="font-medium">{step.title}</p>
                  </TooltipContent>
                </Tooltip>

                {/* Connecting line */}
                {index < steps.length - 1 && (
                  <div
                    className={cn(
                      "h-0.5 w-6 md:w-12 transition-colors duration-200",
                      isCompleted ? "bg-green-600" : "bg-gray-200"
                    )}
                  />
                )}
              </React.Fragment>
            );
          })}
        </div>
      </TooltipProvider>
    </div>
  );
}
